<script setup>
const rand = (a, b) => Math.random() * (b - a) + a;

// Small sparkle bulbs — blue/white palette
const palette = [
    { color: '#ffffff', dark: '#6080cc', glow: '#4a7fff' },
    { color: '#c8d8ff', dark: '#2040aa', glow: '#4060ee' },
    { color: '#90b8ff', dark: '#1030aa', glow: '#3050ee' },
    { color: '#5B8CFF', dark: '#0d2888', glow: '#2040cc' },
    { color: '#a8c8ff', dark: '#2848cc', glow: '#4068ff' },
    { color: '#e0eeff', dark: '#4060bb', glow: '#5080ff' },
];

const bulbs = Array.from({ length: 50 }, (_, i) => {
    const p = palette[Math.floor(Math.random() * palette.length)];
    const size = rand(4, 14);
    const deepFade = Math.random() < 0.4;
    return {
        key: 'b' + i,
        color: p.color,
        dark: p.dark,
        glow: p.glow,
        size,
        x: rand(0, 100),
        y: rand(0, 100),
        opacity: rand(0.75, 1.0),
        fadeMin: deepFade ? +rand(0.08, 0.18).toFixed(2) : +rand(0.30, 0.60).toFixed(2),
        angle: Math.floor(rand(0, 360)),
        beamLen: +(size * rand(10, 22)).toFixed(1),
        dur: +rand(6.0, 18.0).toFixed(2),
        delay: +rand(0, 10.0).toFixed(2),
    };
});

// Large spotlight orbs
const spotlights = Array.from({ length: 12 }, (_, i) => ({
    key: 's' + i,
    size: rand(300, 900),
    x: rand(0, 100),
    y: rand(0, 100),
    opacityMax: +rand(0.05, 0.13).toFixed(2),
    opacityMin: +rand(0.02, 0.05).toFixed(2),
    dur: +rand(10.0, 22.0).toFixed(2),
    delay: +rand(0, 30.0).toFixed(2),
    hue: Math.random() < 0.7 ? '215' : '200',
}));

// Medium spotlight orbs
const midlights = Array.from({ length: 18 }, (_, i) => ({
    key: 'm' + i,
    size: rand(80, 220),
    x: rand(0, 100),
    y: rand(0, 100),
    opacityMax: +rand(0.18, 0.40).toFixed(2),
    opacityMin: +rand(0.06, 0.14).toFixed(2),
    dur: +rand(6.0, 16.0).toFixed(2),
    delay: +rand(0, 24.0).toFixed(2),
    hue: Math.random() < 0.6 ? '215' : Math.random() < 0.5 ? '200' : '230',
}));
</script>

<template>
    <div class="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">

        <!-- Large spotlight orbs -->
        <div
            v-for="s in spotlights"
            :key="s.key"
            class="absolute rounded-full spotlight"
            :style="{
                width: s.size + 'px',
                height: s.size + 'px',
                left: `calc(${s.x}% - ${s.size / 2}px)`,
                top: `calc(${s.y}% - ${s.size / 2}px)`,
                background: `radial-gradient(circle at 50% 50%, #ffffff 0%, hsl(${s.hue},95%,88%) 8%, hsl(${s.hue},85%,65%) 25%, hsl(${s.hue},80%,45%) 50%, transparent 75%)`,
                filter: `blur(${Math.round(s.size * 0.12)}px)`,
                animationDuration: s.dur + 's',
                animationDelay: s.delay + 's',
                '--opacity-max': s.opacityMax,
                '--opacity-min': s.opacityMin,
            }"
        />

        <!-- Medium spotlight orbs -->
        <div
            v-for="m in midlights"
            :key="m.key"
            class="absolute rounded-full spotlight"
            :style="{
                width: m.size + 'px',
                height: m.size + 'px',
                left: `calc(${m.x}% - ${m.size / 2}px)`,
                top: `calc(${m.y}% - ${m.size / 2}px)`,
                background: `radial-gradient(circle at 50% 50%, #ffffff 0%, hsl(${m.hue},95%,88%) 10%, hsl(${m.hue},85%,65%) 30%, hsl(${m.hue},80%,45%) 55%, transparent 80%)`,
                filter: `blur(${Math.round(m.size * 0.10)}px)`,
                animationDuration: m.dur + 's',
                animationDelay: m.delay + 's',
                '--opacity-max': m.opacityMax,
                '--opacity-min': m.opacityMin,
            }"
        />

        <!-- Small spotlight beams -->
        <div
            v-for="b in bulbs"
            :key="b.key"
            class="absolute rounded-full sparkle"
            :style="{
                width: b.size + 'px',
                height: b.size + 'px',
                left: b.x + '%',
                top: b.y + '%',
                opacity: b.opacity,
                backgroundImage: `radial-gradient(circle at 50% 50%, #ffffff 0%, #ffffff 20%, ${b.color} 55%, ${b.dark} 100%)`,
                boxShadow: `0 0 ${(b.size * 2).toFixed(1)}px #ffffffbb, 0 -${b.beamLen}px ${(b.size * 5).toFixed(1)}px -${(b.size * 0.6).toFixed(1)}px ${b.glow}aa, 0 0 ${(b.size * 7).toFixed(1)}px ${b.glow}55`,
                transform: `rotate(${b.angle}deg)`,
                animationDuration: b.dur + 's',
                animationDelay: b.delay + 's',
                '--fade-min': b.fadeMin,
            }"
        />
    </div>
</template>

<style scoped>
.sparkle {
    animation: sparkle-pulse ease-in-out infinite;
}

.spotlight {
    animation: spotlight-pulse ease-in-out infinite;
    animation-fill-mode: backwards;
    opacity: var(--opacity-min, 0.03);
}

@keyframes sparkle-pulse {
    0%   { transform: scale(1);    filter: opacity(1)                    brightness(1); }
    25%  { transform: scale(0.7);  filter: opacity(var(--fade-min, 0.2)) brightness(0.3); }
    50%  { transform: scale(1.05); filter: opacity(1)                    brightness(1.3); }
    75%  { transform: scale(0.8);  filter: opacity(var(--fade-min, 0.2)) brightness(0.4); }
    100% { transform: scale(1);    filter: opacity(1)                    brightness(1); }
}

@keyframes spotlight-pulse {
    0%   { transform: scale(0.92); opacity: var(--opacity-min, 0.03); }
    38%  { transform: scale(1.05); opacity: var(--opacity-max, 0.20); }
    68%  { transform: scale(0.88); opacity: var(--opacity-min, 0.03); }
    84%  { transform: scale(1.10); opacity: var(--opacity-max, 0.20); }
    100% { transform: scale(0.92); opacity: var(--opacity-min, 0.03); }
}
</style>
